<script setup lang="ts">
import { computed, ComputedRef, defineProps } from 'vue';
import DropdownTitleHover from './Dropdown/DropdownTitleHover.vue';
import ContextMenuButton from './ContextMenu/ContextMenuButton.vue';
import Dropdown from './Dropdown/Dropdown.vue';
import CloseCircleIcon from '../icons/CloseCircleIcon.vue';
import InfoIcon from '../icons/InfoIcon.vue';
import TeamUser, { TeamUserType } from '../../../types/teamUser';
import { CurrentUser } from '../../../types/current_user';
import { UserRole, type UserRoleType } from '../../../types/user_role';
import DropDownArrowIcon from '@/dashboard/vue/components/icons/DropdownArrowIcon.vue';
import Divider from '@/dashboard/vue/components/common/Divider.vue';
import FolderIcon from '@/dashboard/vue/components/icons/FolderIcon.vue';
import getRoleEmoji from '@/dashboard/utils/user/getRoleEmoji';
import Tooltip from '@/dashboard/vue/components/common/Tooltip.vue';
import AlertInfoIcon from '@/dashboard/vue/components/icons/AlertInfoIcon.vue';
import UserRoleTooltip from '@/dashboard/vue/components/tooltips/userRoleTooltip.vue';
import { OwnerAdminRoles } from '@/dashboard/constants/roles';

const props = defineProps<{
  user: TeamUser;
  currentUser: CurrentUser;
  canAddCreator: boolean;
  changeRole: (role: UserRoleType, user: TeamUser) => void;
  remove: (user: TeamUser, keepPersonalData: boolean) => void;
}>();

const roles: ComputedRef<UserRoleType[]> = computed(() => {
  let baseRoles: UserRoleType[] = [UserRole.Viewer];
  const teamType: TeamUserType = props.user.teamType;

  if (props.canAddCreator) {
    baseRoles.unshift(UserRole.Creator);
  }

  if (teamType != 'workspace') {
    baseRoles.push(UserRole.Client);
  }

  if (props.currentUser.role === UserRole.Owner && teamType !== 'project') {
    baseRoles.unshift(UserRole.Admin);
  }

  return baseRoles;
});
const canEdit: ComputedRef<boolean> = computed(() => {
  const roles: UserRoleType[] = [UserRole.Owner, UserRole.Admin, UserRole.Creator];

  if (props.user.role === UserRole.Owner || (props.user.role === UserRole.Admin && props.user.teamType !== 'workspace')) {
    return false;
  } else {
    return !(!props.canAddCreator && roles.includes(props.user.role));
  }
});
const canDelete: ComputedRef<boolean> = computed(() => {
  if (props.user.email === props.currentUser.email) {
    return false;
  }

  if (OwnerAdminRoles.includes(props.user.role)) {
    return false;
  }

  if (!props.user.isAdded) {
    return true;
  }

  return canEdit.value;
});
</script>

<template>
  <div class="flex h-[25px] items-center justify-between">
    <div class="flex items-center gap-xs">
      <div class="h-[20px] w-[20px] rounded-10">
        <img :src="props.user.image" alt="" class="h-[20px] w-[20px] rounded-10" />
      </div>
      <span class="text-caption-s flex items-center gap-xs text-black dark:text-gray-500">
        <span v-if="props.user.isOwner()">👑</span>
        <span class="line-clamp-1 block max-w-[210px] text-ellipsis">{{ props.user.email }}</span>
      </span>
    </div>

    <div class="flex items-center">
      <div class="flex items-center justify-end gap-xs">
        <Tooltip>
          <template #title>
            <InfoIcon class="cursor-pointer stroke-gray-400 dark:stroke-gray-650" />
          </template>
          <template #body>
            <UserRoleTooltip :user-role="props.user.role" />
          </template>
        </Tooltip>

        <div v-if="!canEdit" class="flex items-center gap-xs">
          <span class="text-caption-s capitalize text-black dark:text-gray-500">{{ props.user.role }}</span>
        </div>

        <Dropdown v-if="canEdit" :body-class="'z-[6001] right-zero mt-[5px] gap-[2px] p-[6px]'">
          <template #title="{ isOpen }: any">
            <DropdownTitleHover :is-active="isOpen">
              <div class="flex items-center gap-xs">
                <span v-if="!props.user.leaving" class="text-caption-s capitalize text-black dark:text-gray-500">
                  <span>{{ props.user.role }}</span>
                </span>
                <span v-else class="text-caption-s capitalize text-hard-error">On leaving</span>
                <DropDownArrowIcon class="stroke-black dark:stroke-gray-500" />
              </div>
            </DropdownTitleHover>
          </template>
          <template #body>
            <div v-for="role_item in roles" :key="role_item" class="flex flex-col">
              <ContextMenuButton :need-dark="true" :action="() => props.changeRole(role_item, props.user)">
                <div class="flex items-center gap-[10px] whitespace-nowrap">
                  <span class="text-caption-s">{{ getRoleEmoji(role_item) }}</span>
                  <span class="text-caption-s capitalize text-black dark:text-white">{{ role_item }}</span>
                  <Tooltip>
                    <template #title>
                      <InfoIcon class="cursor-pointer stroke-gray-400 dark:stroke-gray-650" />
                    </template>
                    <template #body>
                      <UserRoleTooltip :user-role="role_item" />
                    </template>
                  </Tooltip>
                </div>
              </ContextMenuButton>
            </div>
          </template>
        </Dropdown>

        <button v-if="(canDelete && !user.isAdded) || (user.teamType === 'project' && canDelete)" @click="() => props.remove(props.user, false)">
          <CloseCircleIcon class="stroke-gray-400 dark:stroke-gray-650" />
        </button>

        <Dropdown v-else-if="canDelete && user.isAdded" :body-class="'z-[6001] right-zero mt-[5px]'">
          <template #title>
            <CloseCircleIcon class="stroke-gray-400 dark:stroke-gray-650" />
          </template>
          <template #body>
            <div v-if="false" class="flex flex-col">
              <ContextMenuButton :need-dark="true" :action="() => props.remove(props.user, true)">
                <div class="flex items-center gap-[10px] whitespace-nowrap">
                  <FolderIcon class="stroke-black dark:stroke-white" />
                  <span class="text-caption-s capitalize text-black dark:text-white">Keep Personal dir.</span>
                </div>
              </ContextMenuButton>
              <Divider class="divider-gray-450 dark:divider-gray-700 mx-[8px] !w-auto" />
            </div>
            <div class="flex flex-col">
              <ContextMenuButton :need-dark="true" :action="() => props.remove(props.user, false)">
                <div class="flex items-center gap-[10px] whitespace-nowrap">
                  <CloseCircleIcon class="stroke-hard-error" />
                  <span class="text-caption-s capitalize text-hard-error">Delete completely</span>
                </div>
              </ContextMenuButton>
            </div>
          </template>
        </Dropdown>
      </div>
    </div>
  </div>
</template>
